/* eslint-disable react/static-property-placement, no-param-reassign, no-restricted-syntax */

import { isNumber } from "angular";

import MessageFormat from "messageformat";

export class MessageFormatInterpolationService {
    public static $inject: string[] = [
        "$translateSanitization",
        "$cacheFactory",
        "TRANSLATE_MF_INTERPOLATION_CACHE",
        "localStorageService",
    ];

    public readonly displayName = "$translateMessageFormatInterpolation";

    private readonly $cache: any;

    private $mf: any;

    private readonly $identifier: any;

    constructor(
        private readonly $translateSanitization: any,
        private readonly $cacheFactory: any,
        private readonly TRANSLATE_MF_INTERPOLATION_CACHE: any,
        private readonly localStorageService: angular.local.storage.ILocalStorageService,
    ) {
        this.$cache = $cacheFactory.get(TRANSLATE_MF_INTERPOLATION_CACHE);

        // instantiate with default locale (which is 'en')
        const locale = (this.localStorageService.get("locale") as string) || "en";

        this.$mf = new MessageFormat(locale);
        this.$identifier = "messageformat";

        if (!this.$cache) {
            // create cache if it doesn't exist already
            this.$cache = $cacheFactory(TRANSLATE_MF_INTERPOLATION_CACHE);
        }

        this.$cache.put(locale, this.$mf);
    }

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translateMessageFormatInterpolation#setLocale
     * @methodOf pascalprecht.translate.$translateMessageFormatInterpolation
     *
     * @description
     * Sets current locale (this is currently not use in this interpolation).
     *
     * @param {string} locale Language key or locale.
     */
    public setLocale(locale: any) {
        locale = this.localStorageService.get("locale") || locale;

        this.$mf = this.$cache.get(locale) as typeof MessageFormat;
        if (!this.$mf) {
            const mf = new MessageFormat(locale);
            if (mf) {
                this.$mf = mf;
                this.$cache.put(locale, this.$mf);
            }
        }
    }

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translateMessageFormatInterpolation#getInterpolationIdentifier
     * @methodOf pascalprecht.translate.$translateMessageFormatInterpolation
     *
     * @description
     * Returns an identifier for this interpolation service.
     *
     * @returns {string} $identifier
     */
    public getInterpolationIdentifier() {
        return this.$identifier;
    }

    /**
     * @deprecated will be removed in 3.0
     * @see {@link pascalprecht.translate.$translateSanitization}
     */
    public useSanitizeValueStrategy(value: any) {
        this.$translateSanitization.useStrategy(value);
        return this;
    }

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translateMessageFormatInterpolation#interpolate
     * @methodOf pascalprecht.translate.$translateMessageFormatInterpolation
     *
     * @description
     * Interpolates given string against given interpolate params using MessageFormat.js.
     *
     * @returns {string} interpolated string.
     */
    public interpolate(
        template: string,
        interpolationParams: any,
        context: any,
        sanitizeStrategy: any,
        translationKey: string,
    ) {
        interpolationParams = interpolationParams || {};
        interpolationParams = this.$translateSanitization.sanitize(interpolationParams, "params", sanitizeStrategy);

        let compiledFunction = this.$cache.get(`mf:${template}`);

        // if given string wasn't compiled yet, we do so now and never have to do it again
        if (!compiledFunction) {
            // Ensure explicit type if possible
            // MessageFormat checks the actual type (i.e. for amount based conditions)
            for (const interpolationKey in interpolationParams) {
                if (interpolationParams.hasOwnProperty(interpolationKey)) {
                    // ensure number
                    const num = parseInt(interpolationParams[interpolationKey], 10);
                    if (isNumber(num) && `${num}` === interpolationParams[interpolationKey]) {
                        interpolationParams[interpolationKey] = num;
                    }
                }
            }

            try {
                compiledFunction = this.$mf.compile(template);
                this.$cache.put(`mf:${template}`, compiledFunction);
            } catch (error) {
                const locale = this.localStorageService.get("locale") as string;
                // eslint-disable-next-line
                console.warn(`Translation error for key ${translationKey} and for ${locale} locale.
                Error: ${error.message}`);
            }
        }

        if (compiledFunction) {
            const interpolatedText = compiledFunction(interpolationParams);
            return this.$translateSanitization.sanitize(interpolatedText, "text", sanitizeStrategy);
        }
        return this.$translateSanitization.sanitize(
            `Translation error for key ${translationKey}`,
            "text",
            sanitizeStrategy,
        );
    }
}
// eslint-enable
